import * as React from 'react';
import moment from 'moment';
import {withRouter} from 'react-router5';
import {Router} from 'router5';
import {toast} from 'react-toastify';
import styled from 'styled-components';
import slugify from 'slugify';
import {
  CourseObject,
  CourseStatus,
  createCourse,
  editCourse, getCourseByID,
  getStatusLabel,
  isPublished,
} from 'api/course';
import {FileObject} from 'api/library';
import Switch, {SwitchVariant} from 'components/Switch';
import Container from 'containers/Container';
import PreviewPicker from 'components/PreviewPicker';
import InputTitle from 'components/InputTitle';
import InputDescription from 'components/InputDescription';
import SelectPrice, {CurrencyOptions, SelectPriceProps} from 'components/SelectPrice';
import {InputVariant} from 'components/Input';
import {OptionObject, SelectVariant} from 'components/Select';
import Tabbar from 'components/Tabbar';
import Tab, {TabProps} from 'components/Tab';
import CourseBuilderContext, {CourseBuilderContextValue} from './CourseBuilderContext';
import Button, {ButtonVariant} from 'components/Button';
import Settings from './components/GeneralSettings';
import Landing from './components/LandingSettings';
import Students from './components/Students';
import ContentController from './components/Program';
import isError from 'helpers/is-error';
import validateResponse from 'helpers/validate-response';
import {Currency} from 'types';
import BuilderFooter from 'components/BuilderFooter';

export type CourseBuilderProps =
  {
    courseId?: CourseObject['id'];
  } & { router: Router; }

export interface CourseBuilderState {
  course: Partial<CourseObject>;
}

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  justify-content: space-between;
  flex-direction: column;
  display: flex;
`;

const Header = styled.header`
  margin-bottom: 20px;
`;

const Meta = styled.div`
  padding: 17px 0;
`;

const HeaderGroup = styled.div`
  column-gap: 20px;
  align-items: flex-start;
  grid-template-columns: minmax(min-content, 300px) 1fr;
  grid-auto-flow: column;
  display: grid;
`;

const HeaderSide = styled.div`
  row-gap: 16px;
  display: grid;
`;

const PreviewContainer = styled.div`
  grid-row: span 2;
`;

const HeaderContent = styled.div`
  row-gap: 10px;
  grid-template-rows: max-content 1fr;
  display: grid;
  min-height: 170px;
`;

const TitleContainer = styled.div`
  display: grid;
`;

const DescriptionContainer = styled.div`
  display: grid;
`;

const PriceContainer = styled.div`
  display: grid;
`;

/**
 * The component create or update course object
 */
class CourseBuilder
  extends React.Component<CourseBuilderProps, CourseBuilderState> {
  /**
   * Construct the props
   * @param {CourseBuilderProps} props
   */
  constructor(props: CourseBuilderProps) {
    super(props);

    this.state = {
      course: {},
    };

    this.setCourse = this.setCourse.bind(this);
  }

  componentDidMount() {
    if (this.props.courseId !== undefined) {
      getCourseByID(this.props.courseId).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          this.setState((prevState) => ({
            ...prevState,
            course: response,
          }));
        }
      })
    }
  }

  /**
   * Get default proxy course object
   * @return {Partial<CourseObject>}
   */
  defaultCourse(): Partial<CourseObject> {
    return {
      status: CourseStatus.Draft,
      price: {
        amount: 1000,
        currency: Currency.RUB,
      },
      salesDates: {
        startDate: moment(),
      },
    } as Partial<CourseObject>
  }

  /**
   * This function changes the current course object.
   * @param {Partial<CourseObject>} course
   */
  setCourse(course: Partial<CourseObject>): void {
    this.setState((prevState) => {
      return {
        ...prevState,
        course,
      };
    });
  }

  /**
   * Render the builder
   * @return {JSX.Element}
   */
  render() {
    const {
      props,
      state,
      setCourse,
    } = this;

    const {
      router,
    } = props;

    const {
      course,
    } = state;

    console.log('course', course);

    /**
     * Is the course created
     * @return {boolean}
     */
    function isCreated(): boolean {
      return Boolean(course.id);
    }

    /**
     * Render the header
     * @return {JSX.Element}
     */
    function renderHeader(): JSX.Element {
      /**
       * Render meta information
       * @return {JSX.Element}
       */
      function renderMeta(): JSX.Element {
        /**
         * Render status with switch component
         * @return {JSX.Element}
         */
        function renderStatus(): JSX.Element {
          /**
           * Handle change status
           */
          function handleChange(): void {
            if (course.status === CourseStatus.Public) {
              setCourse({
                ...course,
                status: CourseStatus.Draft,
              });
            } else {
              setCourse({
                ...course,
                status: CourseStatus.Public,
              });
            }
          }

          return (
            <Switch
              variant={SwitchVariant.Accent}
              onChange={handleChange}
              label={course.status && (
                getStatusLabel(course.status)
              )}
              defaultChecked={course.status && (
                isPublished(course.status)
              )}
            />
          );
        }

        return (
          <Meta>
            {renderStatus()}
          </Meta>
        );
      }

      /**
       * Render the header content
       * @return {JSX.Element}
       */
      function renderContent(): JSX.Element {
        /**
         * Render preview
         * @return {JSX.Element}
         */
        function renderPreview(): JSX.Element {
          /**
           * This change event handler.
           * @param {FileObject} file
           */
          function handleChange(file: FileObject): void {
            setCourse({
              ...course,
              preview: {
                id: file.id,
                type: file.type,
                url: file.url,
              },
            });
          }

          return (
            <PreviewContainer>
              <PreviewPicker
                preview={course.preview}
                onChange={handleChange}
              />
            </PreviewContainer>
          );
        }

        /**
         * Render title
         * @return {JSX.Element}
         */
        function renderTitle(): JSX.Element {
          /**
           * Handle title blur
           * @param {React.FocusEvent<HTMLTextAreaElement>} event
           */
          function handleBlur(
              event: React.FocusEvent<HTMLTextAreaElement>,
          ): void {
            setCourse({
              ...course,
              title: event.target.value,
              slug: slugify(event.target.value),
            });
          }

          return (
            <TitleContainer>
              <InputTitle
                placeholder="Введите название курса"
                defaultValue={course.title}
                onBlur={handleBlur}
              />
            </TitleContainer>
          );
        }

        /**
         * Render description
         * @return {JSX.Element}
         */
        function renderDescription(): JSX.Element {
          /**
           * Handle description blur
           * @param {React.FocusEvent<HTMLTextAreaElement>} event
           */
          function handleBlur(
              event: React.FocusEvent<HTMLTextAreaElement>,
          ): void {
            setCourse({
              ...course,
              description: event.target.value,
            });
          }

          return (
            <DescriptionContainer>
              <InputDescription
                placeholder="Введите описание курса"
                defaultValue={course.description}
                onBlur={handleBlur}
              />
            </DescriptionContainer>
          );
        }

        /**
         * Render price
         * @return {JSX.Element}
         */
        function renderPrice(): JSX.Element {
          /**
           * Handle amount blur
           * @param {React.ChangeEvent<HTMLInputElement>} event
           */
          function handleAmountBlur(
              event: React.ChangeEvent<HTMLInputElement>,
          ): void {
            const amount = Number(event.target.value) > 0 ? (
              Number(event.target.value)
            ) : undefined;

            setCourse({
              ...course,
              price: {
                ...course.price,
                amount,
              },
            });
          }

          /**
           * Handle currency change
           * @param {OptionObject | any} option
           */
          function handleCurrencyChange(option: OptionObject | any): void {
            setCourse({
              ...course,
              price: {
                ...course.price,
                currency: option.value,
              },
            });
          }

          const inputProps: SelectPriceProps['inputProps'] = {
            defaultValue: course.price?.amount,
            variant: InputVariant.Accent,
            onBlur: handleAmountBlur,
          };

          const selectProps: SelectPriceProps['selectProps'] = {
            variant: SelectVariant.Accent,
            placeholder: null,
            defaultValue: CurrencyOptions.find((option) => (
              option.value === course.price?.currency
            )),
            onChange: handleCurrencyChange,
          };

          return (
            <PriceContainer>
              <SelectPrice
                inputProps={inputProps}
                selectProps={selectProps}
              />
            </PriceContainer>
          );
        }

        return (
          <HeaderGroup>
            <HeaderSide>
              {renderPreview()}
              {renderPrice()}
            </HeaderSide>
            <HeaderContent>
              {renderTitle()}
              {renderDescription()}
            </HeaderContent>
          </HeaderGroup>
        );
      }

      return (
        <Header>
          {renderMeta()}
          {renderContent()}
        </Header>
      );
    }

    /**
     * Render tabbar
     * @return {JSX.Element}
     */
    function renderTabbar(): JSX.Element {
      const tabs: TabProps[] = [
        {
          name: 'settings',
          title: 'Основные настройки',
          children: <Settings />,
        },
        {
          name: 'landing',
          title: 'Настройки лендинга',
          children: <Landing />,
          disabled: !isCreated(),
        },
        {
          name: 'content',
          title: 'Содержание',
          children: <ContentController />,
          disabled: !isCreated(),
        },
        {
          name: 'students',
          title: 'Студенты',
          children: <Students />,
          disabled: !isCreated(),
        },
        {
          name: 'members',
          title: 'Кураторы',
          children: <div>Кураторы</div>,
          disabled: !isCreated(),
        },
      ];

      return (
        <Tabbar>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              {...tab}
            />
          ))}
        </Tabbar>
      );
    }

    /**
     * Render footer
     * @return {JSX.Element}
     */
    function renderFooter(): JSX.Element {
      /**
       * Send error toast and return false
       * @param {string} error
       * @return {boolean}
       */
      function sendError(error: string) {
        toast.error(error, {
          position: 'top-center',
        });
        return false;
      }

      /**
       * Is the course valid
       * @return {boolean}
       */
      function isCourseValid(): boolean {
        /**
         * Is the settings valid
         * @return {boolean}
         */
        function isSettingsValid(): boolean {
          if (!course.salesDates?.startDate) {
            return sendError('Выберите дату открытия продаж');
          }

          if (course.fixedDate?.active) {
            if (!course.fixedDate.dates?.startDate) {
              return sendError('Выберите дату старта');
            }
          }

          if (course.promocodes?.active) {
            if (course.promocodes.list?.length) {
              for (let i = 0; i < course.promocodes.list?.length; i++) {
                if (!course.promocodes.list[i].code?.length) {
                  return sendError('Введите кодовое слово');
                }

                if (!course.promocodes.list[i].dates?.startDate) {
                  return sendError('Введите дату старта');
                }

                if (!course.promocodes.list[i].discount) {
                  return sendError('Укажите скидку');
                }
              }
            }
          }

          if (course.sale?.active) {
            if (!course.sale.dates?.startDate) {
              return sendError('Выберите дату старта');
            }

            if (!course.sale.price) {
              return sendError('Выберите стоимость');
            }
          }

          if (course.postMessage?.active) {
            if (!course.postMessage.text || !course.postMessage.text?.length) {
              return sendError('Введите сообщение');
            }
          }

          if (course.resale?.active) {
            if (!course.resale.discount) {
              return sendError('Укажите скидку для повторной продажи');
            }
          }

          if (course.finalLink?.active) {
            if (!course.finalLink.link || !course.finalLink.link?.length) {
              return sendError('Укажите ссылку на страницу благодарности');
            }
          }

          return true;
        }

        if (!course.title?.length) {
          return sendError('Введите название курса');
        }

        if (!course.description?.length) {
          return sendError('Введите описание курса');
        }

        return isSettingsValid();
      }

      /**
       * Render create button
       * @return {JSX.Element}
       */
      function renderCreateButton(): JSX.Element {
        /**
         * Handle click on the button
         */
        function handleClick(): void {
          if (!isCourseValid()) return;

          createCourse(course as CourseObject).then((response) => {
            validateResponse(response);

            (response && !isError(response)) && (
              router.navigate('courses.course.edit', {
                id: response.id,
                tab: 'content',
              })
            );
          });
        }

        return (
          <Button variant={ButtonVariant.Primary} onClick={handleClick}>
            Создать и перейти к соодержанию
          </Button>
        );
      }

      /**
       * Render update button
       * @return {JSX.Element}
       */
      function renderUpdateButton(): JSX.Element {
        /**
         * Handle click on the button
         */
        function handleClick(): void {
          if (!isCourseValid()) return;

          editCourse(course as CourseObject).then((response) => {
            validateResponse(response);

            (response && !isError(response)) && (
              toast.success('Курс сохранен')
            );
          });
        }

        return (
          <Button variant={ButtonVariant.Primary} onClick={handleClick}>
            Сохранить
          </Button>
        );
      }

      /**
       * Render the footer content
       * @return {JSX.Element}
       */
      function renderContent(): JSX.Element {
        return isCreated() ? (
          <React.Fragment>
            {renderUpdateButton()}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {renderCreateButton()}
          </React.Fragment>
        );
      }

      return (
        <BuilderFooter>
          {renderContent()}
        </BuilderFooter>
      );
    }

    const {Provider} = CourseBuilderContext;

    /**
     * Get the context value
     * @return {CourseBuilderContextValue}
     */
    function getContextValue(): CourseBuilderContextValue {
      return {
        course,
        setCourse,
      };
    }

    return (
      <Provider value={getContextValue()}>
        <Wrapper>
          <Container>
            {renderHeader()}
            {renderTabbar()}
          </Container>
          {renderFooter()}
        </Wrapper>
      </Provider>
    );
  }
}

export default withRouter(CourseBuilder);
